/**
 * 学习乐园 · 舒尔特方格（game-shuerte.js）
 * ============================================================
 * 「逻辑与推理」组第一作：N×N 方格里 1..N² 乱序，按 1→N² 顺序点完，
 * 计的是从第一次点击到点完最后一格的用时——注意力训练的经典范式。
 *
 * 反馈底线（同本栏目所有游戏）：
 *   点对了 → 该格锁定（划掉感），不抢话；
 *   点错了 → 说清「下一个是几、你点的是几」——错点每一步都是被
 *            方格本身纠正一次，错点数如实计入结算，不扣分不惩罚。
 *
 * 口径（页面与 doc 页同步说明）：
 *   - 计时从第一次点击起表：摆好方格到进入状态的时间不该算进成绩；
 *     用时在每次点击后随进度刷新（不另起定时器——不骚扰读屏，也没必要）；
 *   - 不显示「下一个是几」：显示就是替你找，考的就是顺序扫描；
 *     点错时才告诉你是哪一步——那叫纠正，不叫提示；
 *   - 三档 3×3/4×4/5×5，成绩按档分开存本机。
 *
 * 简化红线：不做中断续局（计时局没有中断续玩的意义），刷新即重开；
 * 成绩只存本机 localStorage，不上传。
 *
 * 冒烟钩子：window.STSchulte 暴露纯引擎函数（shuffled / levelSpec），
 * 供确定性用例；交互路径由 scripts/smoke-study.mjs 的对应 section 覆盖。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-shuerte';
  var RECORD_KEY = 'study-shuerte-record';

  /* ============ 纯引擎（无 DOM） ============ */

  /** 档位规格：3/4/5 → 边长与格数（非法值兜底 5——5×5 是标准舒尔特方格） */
  function levelSpec(level) {
    var n = level === 3 || level === 4 ? level : 5;
    return { n: n, total: n * n };
  }

  /** 1..total 的一个乱序（Fisher–Yates） */
  function shuffled(total) {
    var a = [];
    for (var i = 1; i <= total; i++) a.push(i);
    for (var j = a.length - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var t = a[j]; a[j] = a[k]; a[k] = t;
    }
    return a;
  }

  /* ============ 状态与工具 ============ */

  var root = null;
  var ui = {};
  var S = {
    level: 3, nums: [], expect: 1,
    miss: 0, startAt: 0, done: false,
    soundOn: true, resetArm: false, resetTimer: null,
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function fmtSec(ms) {
    var s = Math.round(ms / 100) / 10;
    return s < 60 ? s + ' 秒' : Math.floor(s / 60) + ' 分 ' + Math.round(s % 60) + ' 秒';
  }

  /* —— 音效（WebAudio 本地合成，与连连看/排排序同一套音色思路）—— */
  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[880, 0]];
      else if (kind === 'bad') seq = [[220, 0]];
      else seq = [[523, 0], [659, 0.09], [784, 0.18]];   /* win：三连琶音 */
      var type = kind === 'bad' ? 'square' : 'sine';
      for (var i = 0; i < seq.length; i++) {
        var osc = actx.createOscillator();
        var g = actx.createGain();
        osc.type = type;
        osc.frequency.value = SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0];
        var t0 = t + seq[i][1];
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.27 : 0.09, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + (kind === 'win' && i === seq.length - 1 ? (SFX_MOBILE ? 0.32 : 0.24) : (SFX_MOBILE ? 0.16 : 0.1)));
        osc.connect(g).connect(actx.destination);
        osc.start(t0); osc.stop(t0 + (SFX_MOBILE ? 0.4 : 0.3));
      }
    } catch (e) { /* 音频不可用静默（自动化/无设备） */ }
  }

  /* —— 偏好与成绩（只存本机）—— */
  function save() {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, level: S.level, soundOn: S.soundOn })); }
    catch (e) { /* 存储不可用静默（无痕模式） */ }
  }
  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (d && d.v === 1) {
        if (d.level === 3 || d.level === 4 || d.level === 5) S.level = d.level;
        S.soundOn = d.soundOn !== false;
      }
    } catch (e) { /* 忽略脏数据 */ }
  }
  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 静默 */ }
  }

  /* ============ 渲染 ============ */

  function renderBoard() {
    var spec = levelSpec(S.level);
    ui.board.style.setProperty('--st-sr-cols', spec.n);
    ui.board.textContent = '';
    for (var i = 0; i < spec.total; i++) {
      (function (idx) {
        var b = el('button', 'st-sr-cell');
        b.type = 'button';
        b.textContent = String(S.nums[idx]);
        b.setAttribute('aria-label', '数字 ' + S.nums[idx]);
        b.addEventListener('click', function () { onCell(b, S.nums[idx]); });
        ui.board.appendChild(b);
      })(i);
    }
  }

  /** 状态行：只在点击/开局/结算时更新（用时随进度重算，不设定时器） */
  function renderStatus() {
    var spec = levelSpec(S.level);
    if (S.done) return;
    var used = S.startAt ? ' · 用时 ' + fmtSec(Date.now() - S.startAt) : '';
    ui.status.textContent = '已找 ' + (S.expect - 1) + ' / ' + spec.total
      + (S.miss ? ' · 点错 ' + S.miss + ' 次' : '') + used;
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    var any = rec[3] && rec[3].best != null || rec[4] && rec[4].best != null || rec[5] && rec[5].best != null;
    if (!any) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '还没完成过——点完一局，这里按档记下你的最快用时。'));
      return;
    }
    box.className = 'st-llk-record';
    [3, 4, 5].forEach(function (lv) {
      var r = rec[lv];
      var spec = levelSpec(lv);
      box.appendChild(recordStat(spec.n + '×' + spec.n + ' 最快',
        r && r.best != null ? fmtSec(r.best) : '—'));
    });
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function renderLevels() {
    ui.levels.textContent = '';
    [3, 4, 5].forEach(function (lv) {
      var spec = levelSpec(lv);
      var b = el('button', 'st-llk-pack-btn' + (S.level === lv ? ' is-active' : ''));
      b.type = 'button';
      b.setAttribute('data-level', String(lv));
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.level === lv ? 'true' : 'false');
      b.textContent = spec.n + '×' + spec.n;
      b.addEventListener('click', function () {
        if (S.level === lv) return;
        S.level = lv;
        renderLevels();
        newGame();
        save();
      });
      ui.levels.appendChild(b);
    });
  }

  /* ============ 交互 ============ */

  function onCell(btn, num) {
    if (S.done) return;
    var spec = levelSpec(S.level);
    if (!S.startAt) S.startAt = Date.now();   /* 计时从第一次点击起表（口径见文件头） */
    if (num === S.expect) {
      btn.disabled = true;                    /* 锁定 = 划掉感，不给「真棒」式抢话 */
      btn.classList.add('is-done');
      if (num === spec.total) { finish(); return; }
      S.expect++;
      renderNote('', '');
      sfx('ok');
    } else {
      S.miss++;
      renderNote('下一个是 ' + S.expect + '，你点的是 ' + num + '。从 1 开始顺着找，找到了就点。', 'warn');
      btn.classList.add('is-bad');
      setTimeout(function () { btn.classList.remove('is-bad'); }, 450);
      sfx('bad');
    }
    renderStatus();
  }

  function renderNote(msg, tone) {
    ui.note.textContent = msg;
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function finish() {
    S.done = true;
    var ms = Date.now() - S.startAt;
    var spec = levelSpec(S.level);
    var rec = loadRecord();
    var r = rec[S.level] || { best: null, plays: 0 };
    r.plays++;
    var isBest = r.best == null || ms < r.best;
    if (isBest) r.best = ms;
    rec[S.level] = r;
    saveRecord(rec);
    ui.status.textContent = '找完了！' + spec.n + '×' + spec.n + ' 用时 ' + fmtSec(ms)
      + (S.miss ? ' · 点错 ' + S.miss + ' 次' : ' · 一次都没点错');
    ui.status.className = 'st-status is-over';
    renderNote((isBest ? '这是 ' + spec.n + '×' + spec.n + ' 档的最好成绩！' : '这个档位你的最好成绩是 ' + fmtSec(r.best) + '。')
      + '错点每一步都被方格纠正过——比快更值得留意的是少走神。', 'ok');
    sfx('win');
    renderRecord();
  }

  function newGame() {
    var spec = levelSpec(S.level);
    S.nums = shuffled(spec.total);
    S.expect = 1;
    S.miss = 0;
    S.startAt = 0;
    S.done = false;
    ui.status.className = 'st-status';
    ui.status.textContent = '按 1 到 ' + spec.total + ' 的顺序点，计时从你点第一格开始。';
    renderNote('不显示下一个是几——显示就是替你找；点错了会告诉你差在哪。', '');
    renderBoard();
    renderRecord();
    renderButtons();
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.board = root.querySelector('[data-st-sr-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.levels = root.querySelector('[data-st-sr-levels]');
    ui.record = root.querySelector('[data-st-record]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    saveRecord({});
    renderRecord();
    renderButtons();
    renderNote('已重置舒尔特方格的本机成绩。', '');
  }

  /* 音效选择器（口径同排排序：tool-segmented 双按钮） */
  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          syncSoundPicker();
          save();
          if (S.soundOn) sfx('ok');
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }
  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* 冒烟/单测钩子：纯函数出全局，DOM 状态不暴露 */
  global.STSchulte = {
    levelSpec: levelSpec,
    shuffled: shuffled,
    shuffledTotal: function (lv) { return shuffled(levelSpec(lv).total); },
  };
})(typeof window !== 'undefined' ? window : this);
